import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../auth/supabaseClient";
import {
  buildGroupBathTreatmentOperation,
  buildTreatmentProtocolUpsertOperation,
  type TreatmentProtocolConfig,
} from "../operations/treatmentProtocolOperations";
import type { TreatmentRoute } from "../operations/workerOperations";
import { getOrCreateDeviceId } from "../sync/deviceIdentity";
import { persistAndSyncOperation } from "../sync/operationRuntime";
import type { OfflineOperation, OutboxSummary } from "../sync/types";
import { ConfirmationSheet } from "./ConfirmationSheet";

interface TreatmentProtocolAdministrationPanelProps {
  farmId: string;
  userId: string;
  onSyncSummary?: (summary: OutboxSummary) => void;
}

interface ProtocolRow {
  protocolId: string;
  protocolCode: string;
  displayName: string;
  route: TreatmentRoute;
  requiresIsolation: boolean;
  requiresSumpDisconnect: boolean;
  managerApprovalRequired: boolean;
  protocolConfig: TreatmentProtocolConfig;
  isActive: boolean;
  domainVersion: number;
}

interface TankRow {
  tankId: string;
  tankCode: string;
  mode?: "connected" | "independent";
  circuitId?: string;
}

interface CircuitRow {
  circuitId: string;
  circuitCode: string;
  displayName: string;
}

interface TreatmentAdministrationSnapshot {
  canManage: boolean;
  protocols: ProtocolRow[];
  tanks: TankRow[];
  circuits: CircuitRow[];
}

const ROUTES: ReadonlyArray<{ value: TreatmentRoute; label: string }> = [
  { value: "bath", label: "حمام" },
  { value: "water", label: "داخل آب" },
  { value: "feed", label: "غذا" },
  { value: "oral", label: "خوراکی" },
  { value: "injection", label: "تزریق" },
  { value: "topical", label: "موضعی" },
  { value: "other", label: "سایر" },
];

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function records(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(record) : [];
}

function firstRecord(value: unknown): Record<string, unknown> | undefined {
  if (Array.isArray(value)) return record(value[0]);
  if (value && typeof value === "object") return record(value);
  return undefined;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function optionalNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return undefined;
}

function routeValue(value: unknown): TreatmentRoute {
  const candidate = String(value ?? "");
  return ROUTES.some((route) => route.value === candidate)
    ? (candidate as TreatmentRoute)
    : "water";
}

function protocolConfig(value: unknown): TreatmentProtocolConfig {
  const item = record(value);
  const medicineName = optionalString(item.medicineName);
  const doseValue = optionalNumber(item.doseValue);
  const doseUnit = optionalString(item.doseUnit);
  const durationDays = optionalNumber(item.durationDays);

  return {
    ...(medicineName ? { medicineName } : {}),
    ...(doseValue === undefined ? {} : { doseValue }),
    ...(doseUnit ? { doseUnit } : {}),
    ...(durationDays === undefined ? {} : { durationDays }),
  };
}

async function loadTreatmentAdministration(
  farmId: string,
): Promise<TreatmentAdministrationSnapshot> {
  const [permissionResult, protocolResult, tankResult, stateResult, circuitResult] =
    await Promise.all([
      supabase.rpc("permission_decide", {
        target_farm_id: farmId,
        requested_permission: "treatment.manage",
      }),
      supabase.rpc("treatment_protocol_admin_list_v1", {
        target_farm_id: farmId,
      }),
      supabase
        .from("tanks")
        .select("id,tank_code,is_active")
        .eq("farm_id", farmId)
        .eq("is_active", true)
        .order("tank_code"),
      supabase.from("tank_water_states").select("tank_id,mode,circuit_id").eq("farm_id", farmId),
      supabase
        .from("water_circuits")
        .select("id,circuit_code,display_name,is_active")
        .eq("farm_id", farmId)
        .eq("is_active", true)
        .order("circuit_code"),
    ]);

  for (const result of [permissionResult, protocolResult, tankResult, stateResult, circuitResult]) {
    if (result.error) throw result.error;
  }

  const permission = firstRecord(permissionResult.data);
  const stateByTank = new Map(
    records(stateResult.data).map((state) => [String(state.tank_id), state]),
  );

  const protocols = records(protocolResult.data).map((item) => ({
    protocolId: String(item.protocol_id),
    protocolCode: String(item.protocol_code ?? ""),
    displayName: String(item.display_name ?? ""),
    route: routeValue(item.route),
    requiresIsolation: item.requires_isolation === true,
    requiresSumpDisconnect: item.requires_sump_disconnect === true,
    managerApprovalRequired: item.manager_approval_required === true,
    protocolConfig: protocolConfig(item.protocol_config),
    isActive: item.is_active === true,
    domainVersion: Number(item.domain_version ?? 1),
  }));

  const tanks = records(tankResult.data).map((item) => {
    const tankId = String(item.id);
    const state = stateByTank.get(tankId);
    const mode =
      state?.mode === "connected" || state?.mode === "independent" ? state.mode : undefined;
    const circuitId = optionalString(state?.circuit_id);

    return {
      tankId,
      tankCode: String(item.tank_code ?? ""),
      ...(mode ? { mode } : {}),
      ...(circuitId ? { circuitId } : {}),
    } satisfies TankRow;
  });

  const circuits = records(circuitResult.data).map((item) => ({
    circuitId: String(item.id),
    circuitCode: String(item.circuit_code ?? ""),
    displayName: String(item.display_name ?? ""),
  }));

  return {
    canManage: permission?.allowed === true,
    protocols,
    tanks,
    circuits,
  };
}

function routeLabel(route: TreatmentRoute): string {
  return ROUTES.find((item) => item.value === route)?.label ?? route;
}

function syncMessage(status: string): string {
  if (status === "synced") return "تغییر درمان با سرور همگام شد.";
  if (status === "pending") return "تغییر در صف آفلاین ثبت شد.";
  if (status === "retryable_error") return "تغییر حفظ شد و دوباره ارسال می‌شود.";
  if (status === "terminal_conflict") return "ثبت نیاز به بررسی تعارض یا محدودیت ایمنی دارد.";
  return "تغییر ثبت شد.";
}

export function TreatmentProtocolAdministrationPanel({
  farmId,
  userId,
  onSyncSummary,
}: TreatmentProtocolAdministrationPanelProps) {
  const [snapshot, setSnapshot] = useState<TreatmentAdministrationSnapshot>();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState<OfflineOperation>();
  const [confirmationParts, setConfirmationParts] = useState<string[]>([]);
  const [pendingKind, setPendingKind] = useState<"protocol" | "group_bath">();

  const [editingProtocolId, setEditingProtocolId] = useState<string>();
  const [protocolCode, setProtocolCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [route, setRoute] = useState<TreatmentRoute>("bath");
  const [medicineName, setMedicineName] = useState("");
  const [doseValue, setDoseValue] = useState("");
  const [doseUnit, setDoseUnit] = useState("");
  const [durationDays, setDurationDays] = useState("");
  const [requiresIsolation, setRequiresIsolation] = useState(false);
  const [requiresSumpDisconnect, setRequiresSumpDisconnect] = useState(false);
  const [managerApprovalRequired, setManagerApprovalRequired] = useState(false);
  const [protocolActive, setProtocolActive] = useState(true);

  const [groupProtocolId, setGroupProtocolId] = useState("");
  const [selectedTankIds, setSelectedTankIds] = useState<string[]>([]);
  const [groupMedicineName, setGroupMedicineName] = useState("");
  const [groupDoseValue, setGroupDoseValue] = useState("");
  const [groupDoseUnit, setGroupDoseUnit] = useState("");
  const [groupDurationDays, setGroupDurationDays] = useState("");
  const [groupNotes, setGroupNotes] = useState("");

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(undefined);
      const next = await loadTreatmentAdministration(farmId);
      setSnapshot(next);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "بارگذاری پروتکل‌های درمان انجام نشد.");
    } finally {
      setLoading(false);
    }
  }, [farmId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const circuitNameById = useMemo(
    () =>
      new Map(
        snapshot?.circuits.map((circuit) => [
          circuit.circuitId,
          `${circuit.circuitCode} · ${circuit.displayName}`,
        ]) ?? [],
      ),
    [snapshot?.circuits],
  );

  const groupProtocol = useMemo(
    () => snapshot?.protocols.find((protocol) => protocol.protocolId === groupProtocolId),
    [groupProtocolId, snapshot?.protocols],
  );

  useEffect(() => {
    if (!groupProtocol) return;
    setGroupMedicineName(groupProtocol.protocolConfig.medicineName ?? "");
    setGroupDoseValue(
      groupProtocol.protocolConfig.doseValue === undefined
        ? ""
        : String(groupProtocol.protocolConfig.doseValue),
    );
    setGroupDoseUnit(groupProtocol.protocolConfig.doseUnit ?? "");
    setGroupDurationDays(
      groupProtocol.protocolConfig.durationDays === undefined
        ? ""
        : String(groupProtocol.protocolConfig.durationDays),
    );
  }, [groupProtocol]);

  function clearProtocolEditor() {
    setEditingProtocolId(undefined);
    setProtocolCode("");
    setDisplayName("");
    setRoute("bath");
    setMedicineName("");
    setDoseValue("");
    setDoseUnit("");
    setDurationDays("");
    setRequiresIsolation(false);
    setRequiresSumpDisconnect(false);
    setManagerApprovalRequired(false);
    setProtocolActive(true);
  }

  function editProtocol(protocol: ProtocolRow) {
    setEditingProtocolId(protocol.protocolId);
    setProtocolCode(protocol.protocolCode);
    setDisplayName(protocol.displayName);
    setRoute(protocol.route);
    setMedicineName(protocol.protocolConfig.medicineName ?? "");
    setDoseValue(
      protocol.protocolConfig.doseValue === undefined
        ? ""
        : String(protocol.protocolConfig.doseValue),
    );
    setDoseUnit(protocol.protocolConfig.doseUnit ?? "");
    setDurationDays(
      protocol.protocolConfig.durationDays === undefined
        ? ""
        : String(protocol.protocolConfig.durationDays),
    );
    setRequiresIsolation(protocol.requiresIsolation);
    setRequiresSumpDisconnect(protocol.requiresSumpDisconnect);
    setManagerApprovalRequired(protocol.managerApprovalRequired);
    setProtocolActive(protocol.isActive);
    setMessage(undefined);
    setError(undefined);
  }

  function prepareProtocolSave() {
    setMessage(undefined);
    setError(undefined);

    if (!snapshot?.canManage) {
      setError("دسترسی مدیریت درمان برای این حساب فعال نیست.");
      return;
    }

    const existing = editingProtocolId
      ? snapshot.protocols.find((protocol) => protocol.protocolId === editingProtocolId)
      : undefined;
    const protocolId = existing?.protocolId ?? crypto.randomUUID();
    const parsedDose = doseValue.trim() ? Number(doseValue) : undefined;
    const parsedDuration = durationDays.trim() ? Number(durationDays) : undefined;

    try {
      const operation = buildTreatmentProtocolUpsertOperation(
        {
          actorId: userId,
          farmId,
          deviceId: getOrCreateDeviceId(),
          performedAt: new Date().toISOString(),
        },
        {
          protocolId,
          expectedVersion: existing?.domainVersion ?? 0,
          protocolCode,
          displayName,
          route,
          requiresIsolation,
          requiresSumpDisconnect,
          managerApprovalRequired,
          protocolConfig: {
            ...(medicineName.trim() ? { medicineName: medicineName.trim() } : {}),
            ...(parsedDose === undefined ? {} : { doseValue: parsedDose }),
            ...(doseUnit.trim() ? { doseUnit: doseUnit.trim() } : {}),
            ...(parsedDuration === undefined ? {} : { durationDays: parsedDuration }),
          },
          isActive: protocolActive,
        },
      );

      setPending(operation);
      setPendingKind("protocol");
      setConfirmationParts([
        existing ? "ویرایش پروتکل درمان" : "ایجاد پروتکل درمان",
        `کد: ${protocolCode.trim().toUpperCase()}`,
        `نام: ${displayName.trim()}`,
        `روش: ${routeLabel(route)}`,
        medicineName.trim() ? `دارو: ${medicineName.trim()}` : "داروی پیش‌فرض: ثبت نشده",
        parsedDose === undefined
          ? "دوز پیش‌فرض: ثبت نشده"
          : `دوز پیش‌فرض: ${parsedDose}${doseUnit.trim() ? ` ${doseUnit.trim()}` : ""}`,
        requiresIsolation ? "نیازمند ایزوله‌سازی" : "ایزوله‌سازی اجباری نیست",
        requiresSumpDisconnect ? "نیازمند قطع از سامپ" : "قطع از سامپ اجباری نیست",
        managerApprovalRequired ? "تأیید مدیر الزامی" : "تأیید مدیر الزامی نیست",
        protocolActive ? "وضعیت: فعال" : "وضعیت: غیرفعال",
      ]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "پروتکل درمان معتبر نیست.");
    }
  }

  function toggleTank(tankId: string) {
    setSelectedTankIds((current) =>
      current.includes(tankId) ? current.filter((value) => value !== tankId) : [...current, tankId],
    );
  }

  function groupBathClientError(protocol: ProtocolRow, tankIds: string[]): string | undefined {
    const selected = new Set(tankIds);
    const selectedTanks = snapshot?.tanks.filter((tank) => selected.has(tank.tankId)) ?? [];

    if (
      (protocol.requiresIsolation || protocol.requiresSumpDisconnect) &&
      selectedTanks.some((tank) => tank.mode === "connected")
    ) {
      return protocol.requiresSumpDisconnect
        ? "این پروتکل نیازمند قطع تانک‌ها از سامپ است."
        : "این پروتکل نیازمند ایزوله‌سازی تانک‌ها است.";
    }

    const touchedCircuitIds = new Set(
      selectedTanks
        .filter((tank) => tank.mode === "connected" && tank.circuitId)
        .map((tank) => tank.circuitId as string),
    );

    for (const circuitId of touchedCircuitIds) {
      const activeCircuitTankIds =
        snapshot?.tanks
          .filter((tank) => tank.mode === "connected" && tank.circuitId === circuitId)
          .map((tank) => tank.tankId) ?? [];

      if (activeCircuitTankIds.some((tankId) => !selected.has(tankId))) {
        return `برای مدار ${circuitNameById.get(circuitId) ?? circuitId} باید همه تانک‌های فعال انتخاب شوند.`;
      }
    }

    return undefined;
  }

  function prepareGroupBath() {
    setMessage(undefined);
    setError(undefined);

    if (!snapshot?.canManage) {
      setError("دسترسی مدیریت درمان برای این حساب فعال نیست.");
      return;
    }

    if (!groupProtocol?.isActive) {
      setError("یک پروتکل فعال انتخاب کنید.");
      return;
    }

    const topologyError = groupBathClientError(groupProtocol, selectedTankIds);
    if (topologyError) {
      setError(topologyError);
      return;
    }

    const parsedDose = groupDoseValue.trim() ? Number(groupDoseValue) : undefined;
    const parsedDuration = groupDurationDays.trim() ? Number(groupDurationDays) : undefined;
    const selectedCodes =
      snapshot.tanks
        .filter((tank) => selectedTankIds.includes(tank.tankId))
        .map((tank) => tank.tankCode)
        .sort() ?? [];

    try {
      const operation = buildGroupBathTreatmentOperation(
        {
          actorId: userId,
          farmId,
          deviceId: getOrCreateDeviceId(),
          performedAt: new Date().toISOString(),
        },
        {
          protocolId: groupProtocol.protocolId,
          tankIds: selectedTankIds,
          medicineName: groupMedicineName,
          route: groupProtocol.route,
          ...(parsedDose === undefined ? {} : { doseValue: parsedDose }),
          ...(groupDoseUnit.trim() ? { doseUnit: groupDoseUnit.trim() } : {}),
          ...(parsedDuration === undefined ? {} : { durationDays: parsedDuration }),
          ...(groupNotes.trim() ? { notes: groupNotes.trim() } : {}),
        },
      );

      setPending(operation);
      setPendingKind("group_bath");
      setConfirmationParts([
        "حمام گروهی",
        `پروتکل: ${groupProtocol.displayName}`,
        `تانک‌ها: ${selectedCodes.join("، ")}`,
        `روش: ${routeLabel(groupProtocol.route)}`,
        `دارو: ${groupMedicineName.trim()}`,
        parsedDose === undefined
          ? "دوز: ثبت نشده"
          : `دوز: ${parsedDose}${groupDoseUnit.trim() ? ` ${groupDoseUnit.trim()}` : ""}`,
        groupProtocol.managerApprovalRequired
          ? "این پروتکل نیازمند تأیید مدیر است."
          : "تأیید مدیر ثبت شد.",
      ]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "حمام گروهی معتبر نیست.");
    }
  }

  async function confirmPending() {
    if (!pending) return;

    try {
      setBusy(true);
      setError(undefined);
      const result = await persistAndSyncOperation(
        supabase,
        pending,
        typeof navigator === "undefined" ? true : navigator.onLine !== false,
      );
      onSyncSummary?.(result.summary);
      setMessage(syncMessage(result.operation.status));
      setPending(undefined);

      if (result.operation.status === "synced") {
        if (pendingKind === "protocol") clearProtocolEditor();
        if (pendingKind === "group_bath") {
          setSelectedTankIds([]);
          setGroupNotes("");
        }
        await refresh();
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "ثبت تغییر درمان انجام نشد.");
    } finally {
      setBusy(false);
      setPendingKind(undefined);
    }
  }

  if (loading && !snapshot) {
    return (
      <section className="section-card">
        <p>در حال بارگذاری پروتکل‌های درمان…</p>
      </section>
    );
  }

  return (
    <section className="section-card" aria-labelledby="treatment-protocol-admin-title">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">M4B3</p>
          <h2 id="treatment-protocol-admin-title">درمان و پروتکل‌ها</h2>
        </div>
        <span className="count-pill">{snapshot?.protocols.length ?? 0} پروتکل</span>
      </div>

      <p className="section-note">
        پروتکل‌ها نسخه‌دار هستند. محدودیت‌های ایزوله‌سازی، قطع سامپ، مدار مشترک و تأیید مدیر دوباره در
        سرور کنترل می‌شوند.
      </p>

      <div className="work-list">
        {(snapshot?.protocols ?? []).map((protocol) => (
          <div className="work-item pending" key={protocol.protocolId}>
            <span className="work-main">
              <strong>
                {protocol.protocolCode} · {protocol.displayName}
              </strong>
              <span>
                {routeLabel(protocol.route)} · نسخه {protocol.domainVersion} ·{" "}
                {protocol.isActive ? "فعال" : "غیرفعال"}
                {protocol.requiresIsolation ? " · ایزوله" : ""}
                {protocol.requiresSumpDisconnect ? " · قطع سامپ" : ""}
                {protocol.managerApprovalRequired ? " · تأیید مدیر" : ""}
              </span>
            </span>
            <button type="button" className="text-button" onClick={() => editProtocol(protocol)}>
              ویرایش
            </button>
          </div>
        ))}
      </div>

      <div className="manager-card">
        <div className="section-title-row">
          <div>
            <p className="section-kicker">پروتکل</p>
            <h3>{editingProtocolId ? "ویرایش پروتکل" : "پروتکل جدید"}</h3>
          </div>
          {editingProtocolId ? (
            <button type="button" className="text-button" onClick={clearProtocolEditor}>
              پروتکل جدید
            </button>
          ) : null}
        </div>

        <div className="operation-field-grid">
          <label>
            کد پروتکل
            <input
              value={protocolCode}
              disabled={Boolean(editingProtocolId)}
              onChange={(event) => setProtocolCode(event.target.value)}
              placeholder="FMC_BATH"
            />
          </label>
          <label>
            نام پروتکل
            <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
          </label>
          <label>
            روش
            <select
              value={route}
              onChange={(event) => setRoute(event.target.value as TreatmentRoute)}
            >
              {ROUTES.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            داروی پیش‌فرض
            <input value={medicineName} onChange={(event) => setMedicineName(event.target.value)} />
          </label>
          <label>
            دوز پیش‌فرض
            <input
              type="number"
              step="any"
              min="0"
              value={doseValue}
              onChange={(event) => setDoseValue(event.target.value)}
            />
          </label>
          <label>
            واحد دوز
            <input value={doseUnit} onChange={(event) => setDoseUnit(event.target.value)} />
          </label>
          <label>
            مدت (روز)
            <input
              type="number"
              min="1"
              step="1"
              value={durationDays}
              onChange={(event) => setDurationDays(event.target.value)}
            />
          </label>
        </div>

        <div className="operation-checks">
          <label>
            <input
              type="checkbox"
              checked={requiresIsolation}
              onChange={(event) => setRequiresIsolation(event.target.checked)}
            />
            نیازمند ایزوله‌سازی
          </label>
          <label>
            <input
              type="checkbox"
              checked={requiresSumpDisconnect}
              onChange={(event) => setRequiresSumpDisconnect(event.target.checked)}
            />
            نیازمند قطع از سامپ
          </label>
          <label>
            <input
              type="checkbox"
              checked={managerApprovalRequired}
              onChange={(event) => setManagerApprovalRequired(event.target.checked)}
            />
            تأیید مدیر الزامی
          </label>
          <label>
            <input
              type="checkbox"
              checked={protocolActive}
              onChange={(event) => setProtocolActive(event.target.checked)}
            />
            پروتکل فعال است
          </label>
        </div>

        <button
          type="button"
          className="secondary-button"
          disabled={busy || !snapshot?.canManage}
          onClick={prepareProtocolSave}
        >
          بررسی و ثبت پروتکل
        </button>
      </div>

      <div className="manager-card">
        <p className="section-kicker">حمام گروهی</p>
        <h3>ثبت درمان چند تانک</h3>

        <div className="operation-field-grid">
          <label>
            پروتکل فعال
            <select
              value={groupProtocolId}
              onChange={(event) => setGroupProtocolId(event.target.value)}
            >
              <option value="">انتخاب پروتکل</option>
              {(snapshot?.protocols ?? [])
                .filter((protocol) => protocol.isActive)
                .map((protocol) => (
                  <option key={protocol.protocolId} value={protocol.protocolId}>
                    {protocol.protocolCode} · {protocol.displayName}
                  </option>
                ))}
            </select>
          </label>
          <label>
            دارو
            <input
              value={groupMedicineName}
              onChange={(event) => setGroupMedicineName(event.target.value)}
            />
          </label>
          <label>
            دوز
            <input
              type="number"
              min="0"
              step="any"
              value={groupDoseValue}
              onChange={(event) => setGroupDoseValue(event.target.value)}
            />
          </label>
          <label>
            واحد
            <input
              value={groupDoseUnit}
              onChange={(event) => setGroupDoseUnit(event.target.value)}
            />
          </label>
          <label>
            مدت (روز)
            <input
              type="number"
              min="1"
              step="1"
              value={groupDurationDays}
              onChange={(event) => setGroupDurationDays(event.target.value)}
            />
          </label>
          <label>
            توضیح
            <input value={groupNotes} onChange={(event) => setGroupNotes(event.target.value)} />
          </label>
        </div>

        <div className="operation-checks">
          {(snapshot?.tanks ?? []).map((tank) => (
            <label key={tank.tankId}>
              <input
                type="checkbox"
                checked={selectedTankIds.includes(tank.tankId)}
                onChange={() => toggleTank(tank.tankId)}
              />
              {tank.tankCode}
              {tank.mode === "connected"
                ? ` · ${circuitNameById.get(tank.circuitId ?? "") ?? "مدار مشترک"}`
                : tank.mode === "independent"
                  ? " · مستقل"
                  : " · وضعیت آب نامشخص"}
            </label>
          ))}
        </div>

        <p className="section-note">
          برای تانک‌های متصل به مدار مشترک، همه تانک‌های فعال همان مدار باید با هم انتخاب شوند. پروتکل
          ایزوله یا قطع‌سامپ فقط روی تانک‌های مستقل قابل اجراست.
        </p>

        <button
          type="button"
          className="secondary-button"
          disabled={busy || !snapshot?.canManage || !groupProtocolId}
          onClick={prepareGroupBath}
        >
          بررسی و ثبت حمام گروهی
        </button>
      </div>

      {message ? <p className="operation-success">{message}</p> : null}
      {error ? <p className="settings-warning">{error}</p> : null}

      {pending ? (
        <ConfirmationSheet
          title={pendingKind === "group_bath" ? "تأیید حمام گروهی" : "تأیید پروتکل درمان"}
          parts={confirmationParts}
          speechEnabled
          fastConfirmEnabled
          onCancel={() => {
            setPending(undefined);
            setPendingKind(undefined);
          }}
          onConfirm={() => void confirmPending()}
        />
      ) : null}
    </section>
  );
}
